import { useLocation } from 'react-router-dom';
import { useState, useMemo } from 'react';

import Typography from '~/shared/components/Typography';
import SearchBar from '~/shared/components/SearchBar';
import { buildSearchInputId } from '~/shared/components/searchInputMeta';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { useOrganizationStatus } from '~/shared/utils/organizationUtils';

import { RoleArnRow } from './RoleArnRow';
import Tag from '~/shared/components/Tag';
import Skeleton from 'react-loading-skeleton';

export function RoleArnOrganization() {
  const search = useLocation().search;
  const showDevTools = !!new URLSearchParams(search).get('devtools');
  const { accounts, activeTenantId, activeTenantName } = useAuthContext();
  const { childAccounts } = useOrganizationStatus(accounts, activeTenantId);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredAccounts = useMemo(() => {
    if (!searchTerm.trim()) {
      return childAccounts;
    }

    const normalizedSearch = searchTerm.trim().toLowerCase();
    return childAccounts.filter((account) =>
      account.accountName.toLowerCase().includes(normalizedSearch)
    );
  }, [childAccounts, searchTerm]);

  return (
    <div className="px-3.5 flex flex-col relative h-full max-h-[calc(100vh-96px)]">
      <div className="flex flex-col gap-2 py-7.5">
        <Typography variant="H4-med" fontWeight={500} className="text-primary-900">
          Role ARNs
        </Typography>
        <div className="flex items-center gap-2">
          {activeTenantName ? (
            <Tag
              text={activeTenantName || ''}
              variant="default"
              className="text-gray-700!"
            />
          ) : (
            <Skeleton
              containerClassName=" block leading-[unset!important]"
              className="h-7 leading-[unset!important] rounded-[8px] "
              baseColor="var(--color-gray-200)"
              highlightColor="#FFFF"
              width={80}
            />
          )}
          <Typography variant="p3" fontWeight={400} color="text-gray-700">
            ID: {activeTenantId}
          </Typography>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex justify-between pb-4.5 sticky top-28 z-10 ">
          <div className="flex flex-col">
            <Typography
              variant="H5"
              fontWeight={500}
              color="text-primary-900"
              className="px-0.5 py-1.5"
            >
              {filteredAccounts.length} Account{filteredAccounts.length !== 1 ? 's' : ''}
            </Typography>
            <Typography variant="p3" fontWeight={400} color="text-gray-700">
              Input the ARN of the IAM role that provides Datafy permissions that matches each account.
            </Typography>
          </div>
          <SearchBar
            size="sm"
            inputId={buildSearchInputId('settings', 'permissions-org')}
            aria-label="Search organizations"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search"
          />
        </div>

        <div
          className="flex-1 flex flex-col overflow-y-scroll side-panel-scrollbar"
        // style={{ direction: 'rtl' }}
        >
          <div style={{ direction: 'ltr' }}>
            {filteredAccounts.map((account) => (
              <RoleArnRow
                key={account.accountId}
                showDevTools={showDevTools}
                accountName={account.accountName}
                accountId={account.accountId}
                isOrganization
                variant="OrganizationAccountView"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
